import { useEffect, useState } from "react";
import { twJoin } from "tailwind-merge";

import { Accordion } from "@/design-system/Accordion";
import { IconButton } from "@/design-system/IconButton";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { askNobiChatMutations } from "@/queries/ask-nobi/mutations";
import { type TranslationFunction, useTranslation } from "@/translations";

import { useFlashToast } from "../FlashToast/FlashToast";
import { AlertCircleIcon } from "../Icons/AlertCircleIcon";
import { CheckCircleIcon } from "../Icons/CheckCircleIcon";
import { Copy01Icon } from "../Icons/Copy01Icon";
import { ThumbsDownIcon } from "../Icons/ThumbsDownIcon";
import { ThumbsUpIcon } from "../Icons/ThumbsUpIcon";
import { LazyMarkdownRenderer } from "../MarkdownRenderer/LazyMarkdownRenderer";
import { AskNobiNegativeFeedbackModal } from "./AskNobiNegativeFeedbackModal";
import { formatActivityReasoning } from "./helpers/activity";
import type {
  AskNobiActivityPayload,
  AskNobiChatMessagePayload,
  AskNobiPipelineStage,
  AskNobiToolName,
} from "./helpers/types";

interface AskNobiChatMessageProps {
  message: AskNobiChatMessagePayload;
  conversationId: string | undefined;
}

export function AskNobiChatMessage({ message, conversationId }: AskNobiChatMessageProps): React.ReactNode | null {
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const [rating, setRating] = useState<"positive" | "negative" | undefined>(
    message.type === "text" ? message.myRating : undefined,
  );
  const [isNegativeModalOpen, setIsNegativeModalOpen] = useState(false);
  const rateMessage = askNobiChatMutations.useRateMessage();

  async function handlePositiveRating() {
    if (!conversationId) {
      return;
    }

    setRating("positive");
    try {
      await rateMessage.mutateAsync({
        conversationId,
        messageId: message.id,
        data: { rating: "positive" },
      });
    } catch {
      setRating(undefined);
    }
  }

  async function handleNegativeRating() {
    if (!conversationId) {
      return;
    }

    setRating("negative");
    setIsNegativeModalOpen(true);

    try {
      await rateMessage.mutateAsync({
        conversationId,
        messageId: message.id,
        data: { rating: "negative" },
      });
    } catch {
      setRating(undefined);

      return;
    }
  }

  if (message.type === "text" && !message.content.trim()) {
    return null;
  }

  const messageContent = message.type === "text" ? message.content : undefined;
  const showActions = message.type === "text" && message.sender === "assistant" && !message.streaming;

  return (
    <article
      className={twJoin(
        "inline-flex flex-col gap-space-sm rounded-xl pt-card-compact text-text-primary",
        !showActions && "pb-card-compact",
        // oxlint-disable-next-line tailwindcss/no-unknown-classes -- TODO: `peer:` is not a variant, so pt-0! emits no CSS
        message.type === "tool_execution" && "peer peer:pt-0!",
        message.sender === "user" &&
          "max-w-measure-reading self-end bg-background-message-user px-card-compact leading-chat-message",
      )}
    >
      <AskNobiChatMessageContent message={message} />

      {showActions && (
        <footer className="flex w-full flex-1 items-center gap-space-xs pb-card-compact">
          <IconButton
            variant="ghostSecondary"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(messageContent ?? "");
                showFlashToast({
                  variant: "success",
                  message: t("common.copy-to-clipboard.success"),
                });
              } catch (error) {
                console.error("Failed to copy to clipboard:", error);
                showFlashToast({
                  variant: "alert",
                  message: t("common.copy-to-clipboard.failed"),
                });
              }
            }}
            icon={<Copy01Icon />}
            title={t("common.copy-to-clipboard")}
            size="sm"
          />

          <RatingButtons
            rating={rating}
            isPending={rateMessage.isPending}
            conversationId={conversationId}
            onPositive={() => handlePositiveRating()}
            onNegative={() => void handleNegativeRating()}
          />
        </footer>
      )}

      {showActions && conversationId && (
        <AskNobiNegativeFeedbackModal
          isOpen={isNegativeModalOpen}
          onClose={() => setIsNegativeModalOpen(false)}
          conversationId={conversationId}
          messageId={message.id}
          onRated={() => setRating("negative")}
        />
      )}
    </article>
  );
}

function RatingButtons({
  rating,
  isPending,
  conversationId,
  onPositive,
  onNegative,
}: {
  rating: "positive" | "negative" | undefined;
  isPending: boolean;
  conversationId: string | undefined;
  onPositive: () => void;
  onNegative: () => void;
}): React.ReactNode {
  const { t } = useTranslation();

  if (rating === "positive") {
    return (
      <Inline as="span" align="center" wrap={false}>
        <IconButton
          variant="ghostSecondary"
          icon={<ThumbsUpIcon />}
          title={t("component.ask-nobi-chat.message.rating.positive")}
          withTooltip={false}
          size="sm"
          disabled
        />
        <Text variant="overline" tone="disabled">
          {t("component.ask-nobi-chat.message.rating.thanks")}
        </Text>
      </Inline>
    );
  }

  if (rating === "negative") {
    return (
      <Inline as="span" align="center" wrap={false}>
        <IconButton
          variant="ghostSecondary"
          icon={<ThumbsDownIcon />}
          title={t("component.ask-nobi-chat.message.rating.negative")}
          withTooltip={false}
          size="sm"
          disabled
        />
        <Text variant="overline" tone="disabled">
          {t("component.ask-nobi-chat.message.rating.thanks")}
        </Text>
      </Inline>
    );
  }

  return (
    <>
      <IconButton
        variant="ghostSecondary"
        onClick={onPositive}
        icon={<ThumbsUpIcon />}
        title={t("component.ask-nobi-chat.message.rating.positive")}
        size="sm"
        disabled={isPending || !conversationId}
      />
      <IconButton
        variant="ghostSecondary"
        onClick={onNegative}
        icon={<ThumbsDownIcon />}
        title={t("component.ask-nobi-chat.message.rating.negative")}
        size="sm"
        disabled={isPending || !conversationId}
      />
    </>
  );
}

function AskNobiChatMessageContent({ message }: { message: AskNobiChatMessagePayload }): React.ReactNode {
  const { t } = useTranslation();

  switch (message.type) {
    case "text":
      if (message.sender === "user") {
        return <span className="whitespace-pre-wrap">{message.content}</span>;
      }

      return <LazyMarkdownRenderer markdown={message.content} />;
    case "tool_execution":
      return (
        <Stack gap="sm">
          {message.status === "running" && (
            <span className="flex flex-row flex-wrap items-center gap-space-sm text-text-tertiary">
              <span>{getToolName(t, message.toolName)}...</span>
              <Spinner size="xs" />{" "}
            </span>
          )}
          {message.status === "done" && (
            <span className="text-text-tertiary">{getToolName(t, message.toolName)} ✅</span>
          )}
          {message.status === "error" && (
            <span className="text-text-tertiary">{getToolName(t, message.toolName)} ❌</span>
          )}
        </Stack>
      );
    case "activity":
      return <AskNobiActivity message={message} />;
    case "error":
      return (
        <aside className="text-text-danger-secondary">
          <LazyMarkdownRenderer markdown={message.content} />
        </aside>
      );
    default:
      return null;
  }
}

function AskNobiActivity({ message }: { message: AskNobiActivityPayload }): React.ReactNode {
  const { t } = useTranslation();
  const latestItem = message.items.at(-1);
  const elapsedSeconds = useElapsedSeconds(message.startedAt, message.completedAt);
  const latestLabel = latestItem ? getActivityItemLabel(t, latestItem, true) : t("component.ai.thinking");

  return (
    <Accordion.Root variant="highlighted">
      <Accordion.Item value="activity">
        <Accordion.Trigger>
          <span className="flex items-center gap-space-sm">
            <span>{latestLabel}</span>
            {elapsedSeconds >= 3 && (
              <span className="shrink-0 text-caption text-text-tertiary">
                {t("component.ai.activity.elapsed", { seconds: elapsedSeconds })}
              </span>
            )}
          </span>
        </Accordion.Trigger>
        <Accordion.Content>
          <Stack as="ol" gap="sm">
            {message.items.map((item, index) => (
              <li
                className="flex items-center gap-space-sm text-caption text-text-secondary"
                key={getActivityItemKey(item, index)}
              >
                <span className="flex size-icon-inline shrink-0 items-center justify-center">
                  {item.status === "running" && <Spinner size="xs" />}
                  {item.status === "done" && <CheckCircleIcon size={16} tone="success" />}
                  {item.status === "error" && <AlertCircleIcon size={16} tone="danger-secondary" />}
                </span>
                <span>{getActivityItemLabel(t, item, item.status !== "running")}</span>
              </li>
            ))}
          </Stack>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
}

function useElapsedSeconds(startedAt: number, completedAt: number | undefined): number {
  const [now, setNow] = useState(completedAt ?? startedAt);

  useEffect(() => {
    if (completedAt) {
      return;
    }

    const interval = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(interval);
  }, [completedAt]);

  return Math.max(0, Math.floor(((completedAt ?? now) - startedAt) / 1000));
}

function getActivityItemKey(item: AskNobiActivityPayload["items"][number], index: number): string {
  return item.type === "tool" ? `tool-${item.callId}` : `stage-${item.stage}-${index}`;
}

function getActivityItemLabel(
  t: TranslationFunction,
  item: AskNobiActivityPayload["items"][number],
  includeReasoning: boolean,
): string {
  if (item.type === "tool") {
    return getToolName(t, item.toolName);
  }

  if (item.stage === "generating" && item.status === "done") {
    return t("component.ai.stage.generated");
  }

  return includeReasoning && item.status !== "error" && item.reasoning
    ? formatActivityReasoning(item.reasoning)
    : getPipelineStageName(t, item.stage);
}

function getPipelineStageName(t: TranslationFunction, stage: AskNobiPipelineStage): string {
  switch (stage) {
    case "planning":
      return t("component.ai.stage.planning");
    case "fetching":
      return t("component.ai.stage.fetching");
    case "evaluating":
      return t("component.ai.stage.evaluating");
    case "generating":
      return t("component.ai.stage.generating");
  }
}

function getToolName(t: TranslationFunction, toolName: AskNobiToolName): string {
  switch (toolName) {
    case "AddressesPlugin.FetchAddressesWithEnergyLabels":
      return t("component.ai.tools.fetch-addresses");

    case "AssistantConversationsPlugin.FetchAssistantConversations":
      return t("component.ai.tools.fetch-assistant-conversations");

    case "AssistantConversationsPlugin.FetchAssistantConversationMessages":
      return t("component.ai.tools.fetch-assistant-conversation-messages");

    case "CalculatorPlugin.Calculate":
      return t("component.ai.tools.calculate");

    case "HubSpotPlugin.GetHubSpotContactDetails":
      return t("component.ai.tools.get-hubspot-contact-details");

    case "HubSpotPlugin.GetHubSpotCompanyDetails":
      return t("component.ai.tools.get-hubspot-company-details");

    case "WebSearchPlugin.SearchWebAsync":
      return t("component.ai.tools.search-web");

    case "BookableAssetsPlugin.FetchBookableAssets":
      return t("component.ai.tools.fetch-bookable-assets");

    case "BookingsPlugin.FetchBookings":
      return t("component.ai.tools.fetch-bookings");

    case "CompaniesPlugin.FetchCompanies":
      return t("component.ai.tools.fetch-companies");

    case "FeelingAtHomePlugin.FetchFeelingAtHomeData":
      return t("component.ai.tools.fetch-feeling-at-home-data");

    case "ServicePartnersPlugin.FetchServicePartners":
      return t("component.ai.tools.fetch-service-partners");

    case "ChatsPlugin.FetchChats":
      return t("component.ai.tools.fetch-chats");

    case "CommunityFeedPlugin.FetchCommunityPosts":
      return t("component.ai.tools.fetch-community-posts");

    case "EventsPlugin.FetchEvents":
      return t("component.ai.tools.fetch-events");

    case "MessagesPlugin.GetMessageDetailsById":
      return t("component.ai.tools.get-message-details");

    case "PollsPlugin.GetPollDetailsById":
      return t("component.ai.tools.get-poll-details");

    case "LivabilityPlugin.GetLivabilityScores":
      return t("component.ai.tools.get-livability-scores");

    case "ProjectStatisticsPlugin.GetProjectStatistics":
      return t("component.ai.tools.get-project-statistics");

    case "SurveysPlugin.FetchSurveys":
      return t("component.ai.tools.fetch-surveys");

    case "SurveysPlugin.GetSurveyDetailsById":
      return t("component.ai.tools.get-survey-details");

    case "TicketsPlugin.FetchTickets":
      return t("component.ai.tools.fetch-tickets");

    case "TicketsPlugin.GetTicketDetailsById":
      return t("component.ai.tools.get-ticket-details");

    default:
      return toolName;
  }
}
